/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/button';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { requireLegacyWRM } from '@confluence/wrm/entry-points/requireLegacyWRMHelpers.ts';

type User = {
	userId: string;
	avatarUrl: string;
	displayName: string;
};

type LegacyContentModalProps = {
	onClose: (val: boolean) => void;
	content: string;
	user: User;
	webResources: [];
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	paddingTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	paddingLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		paddingTop: token('space.050'),
	},
});

const closeButton = defineMessage({
	id: 'comment-extension-handlers.legacy-content-modal.close-button.non-final-ai',
	defaultMessage: 'Close',
	description:
		'The text is shown as a button label to close the legacy content modal dialog. Used in the modal footer to dismiss the dialog.',
});

export const LegacyContentModal = ({
	onClose,
	content,
	user,
	webResources,
}: LegacyContentModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [containerRef, setContainerRef] = useState<HTMLSpanElement | null>(null);
	useEffect(() => {
		requireLegacyWRM(
			webResources,
			() => {},
			() => {},
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (containerRef) {
			void evalLegacyConnectInlineScripts(containerRef);
		}
	}, [containerRef]);

	const onRefSet = (ref: any) => {
		setContainerRef(ref);
	};

	const handleClose = () => {
		onClose(false);
	};

	const handleImageClick = (event: any) => {
		// clicking on an image shows a full screen modal
		// of the image in the background
		if (event.target instanceof HTMLImageElement) {
			onClose(false);
		}
	};

	return (
		<ModalDialog onClose={handleClose} width="large" testId="modal-dialog-legacy-content">
			{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
			<ModalHeader hasCloseButton />
			<ModalBody>
				<Container>
					<ProfileAvatar userId={user.userId} src={user.avatarUrl} name={user.displayName} />
					<ContentContainer onClick={handleImageClick}>
						<ProfileLink userId={user.userId} fullName={user.displayName} />
						<span ref={onRefSet} dangerouslySetInnerHTML={{ __html: content }} />
					</ContentContainer>
				</Container>
			</ModalBody>
			<ModalFooter>
				<Button autoFocus appearance="primary" onClick={handleClose}>
					{formatMessage(closeButton)}
				</Button>
			</ModalFooter>
		</ModalDialog>
	);
};
